import { Link, Outlet, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, FileClock, LayoutGrid, LogOut, Package, Send, ShieldCheck, Sparkles, Users, type LucideIcon } from 'lucide-react';
import type { AdminIdentity } from '@bounded/api-client';
import { api, isMock } from '../api/client';
import { q } from '../api/queries';
import { requireAdmin } from '../lib/auth';
import { cn } from '@bounded/ui/cn';
import { Mark } from '@bounded/ui/auth-layout';
import { LanguageSelect } from '@bounded/ui/language';
import { useT, type Messages } from '../i18n';

export const Route = createFileRoute('/_app')({
  beforeLoad: ({ context, location }) => requireAdmin(context.queryClient, location.href),
  component: Shell,
});

const nav: { to: string; label: keyof Messages['nav']; icon: LucideIcon; exact?: boolean; visible?: (me: AdminIdentity) => boolean }[] = [
  { to: '/', label: 'overview', icon: LayoutGrid, exact: true },
  { to: '/accounts', label: 'accounts', icon: Users },
  { to: '/devices', label: 'devices', icon: Boxes },
  { to: '/operations', label: 'operations', icon: Send },
  { to: '/releases', label: 'releases', icon: Package },
  { to: '/models', label: 'models', icon: Sparkles },
  { to: '/admins', label: 'admins', icon: ShieldCheck, visible: (me) => me.permissions.manage_admins },
  { to: '/audit', label: 'audit', icon: FileClock },
];

function Shell() {
  const t = useT();
  const me = useQuery(q.me());
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const signOut = useMutation({ mutationFn: () => api.signOut(), onSettled: async () => { queryClient.clear(); await navigate({ to: '/sign-in', search: { next: undefined } }); } });
  const identity = me.data;

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[13.5rem_1fr]">
      <aside className="sticky top-0 z-10 flex flex-col border-b border-rule bg-sunken lg:h-dvh lg:border-r lg:border-b-0">
        <div className="flex h-12 items-center gap-2 px-4 lg:h-auto lg:py-3.5">
          <Mark size={18} />
          <div className="grid leading-tight">
            <span className="text-lg font-semibold tracking-[-0.01em]">Bounded</span>
            <span className="text-xs text-graphite">{t.shell.product}</span>
          </div>
          {isMock && <span className="ml-auto rounded border border-dashed border-rule-strong px-1.5 text-xs text-graphite" title={t.common.fixturesTitle}>{t.common.fixtures}</span>}
          {/* Below lg the account footer is hidden; keep language and sign-out reachable in the top bar. */}
          <div className={cn('flex items-center gap-3 lg:hidden', !isMock && 'ml-auto')}>
            <LanguageSelect compact />
            <button type="button" onClick={() => signOut.mutate()} aria-label={t.common.signOut} title={t.common.signOut} className="text-graphite hover:text-ink"><LogOut size={16} aria-hidden /></button>
          </div>
        </div>
        <nav aria-label={t.shell.nav} className="flex gap-0.5 overflow-x-auto px-2 pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
          {nav.filter((n) => !n.visible || (identity && n.visible(identity))).map(({ to, label, icon: Icon, exact }) => (
            <Link key={to} to={to} activeOptions={{ exact }}
              className="flex h-8 shrink-0 items-center gap-2.5 rounded-[var(--radius-control)] px-2.5 text-graphite hover:bg-paper hover:text-ink data-[status=active]:bg-surface data-[status=active]:font-medium data-[status=active]:text-ink data-[status=active]:shadow-[inset_0_0_0_1px_var(--rule)]">
              <Icon size={16} strokeWidth={1.75} aria-hidden />{t.nav[label]}
            </Link>
          ))}
        </nav>
        {/* Account: who is signed in (with role), then one row of controls. */}
        <div className="mt-auto hidden border-t border-rule lg:block">
          <div className="grid min-w-0 gap-0.5 px-3 py-3">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <p className="truncate text-sm font-medium">{identity?.name}</p>
              {identity && <span className="shrink-0 rounded border border-rule-strong px-1.5 text-xs text-graphite">{t.roles[identity.role]}</span>}
            </div>
            <p className="truncate text-sm text-graphite" title={identity?.email}>{identity?.email}</p>
            {identity && !identity.permissions.mutate && <p className="text-xs text-graphite">{t.shell.readOnly}</p>}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-rule px-3 py-2">
            <LanguageSelect compact />
            <button type="button" onClick={() => signOut.mutate()} className="-mr-1.5 inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-control)] px-1.5 text-sm text-graphite hover:bg-surface hover:text-ink"><LogOut size={14} aria-hidden />{t.common.signOut}</button>
          </div>
        </div>
      </aside>
      <main className="min-w-0 px-4 py-6 sm:px-8 lg:py-8">
        <Outlet />
      </main>
    </div>
  );
}

/** The signed-in admin (loaded by the route guard, so always present under /_app). */
export function useMe() {
  return useQuery(q.me()).data!;
}
